import React from 'react';
import MobileAppConfiguration from './mobileAppConfiguration';
import TypedSelect from 'common/ui/TypedSelect';
import { Form} from 'react-bootstrap';
import AddressEditor from '../AddressEditor';
import Address from 'domain/address';
import InfoIcon from 'common/ui/InfoIcon';
import { MobileThemeOptions } from './mobileTheme';

import './MobileAppConfigurationEditor.scss';

export interface MobileAppConfigurationEditorProps {
    config: MobileAppConfiguration;
    onChange(config: MobileAppConfiguration): void;
}

const MobileAppConfigurationEditor: React.FC<MobileAppConfigurationEditorProps> = props => {

    function onFeatureChange(setter: ((config: MobileAppConfiguration) => void)) {
        const newConfig = {...props.config};
        setter(newConfig);
        props.onChange(newConfig);
    }

    return <div className="mobile-app-config-editor">
        <h2>Merchant Info</h2>

        <div className="row">
            <TextRow label="ID" value={props.config.id} 
                onChange={val => props.onChange({...props.config, id: val })}
                col="col-5"
                />
            <TextRow label="Name" value={props.config.name} 
                onChange={val => props.onChange({...props.config, name: val })}
                col="col-5"
                />
        </div>
        <div className="row">
            <TextRow label="Email" value={props.config.email} 
                onChange={val => props.onChange({...props.config, email: val })} 
                col={"col-5"}
                />
            <TextRow label="Phone" value={props.config.phone} 
                onChange={val => props.onChange({...props.config, phone: val })} 
                col={"col-5"}
                />
        </div>

        <div className="row">
            <TextRow label="Website" value={props.config.websiteUrl} 
                onChange={val => props.onChange({...props.config, websiteUrl: val })} 
                placeholder="https://"
                />
        </div>

        <div className="row">
            <EditorRow label="Address">
                <div className="col-11">
                    <AddressEditor address={props.config.address ?? new MerchantAddress()}
                        setAddress={addr => props.onChange({...props.config, address: addr})}
                        />
                </div>
            </EditorRow>
        </div>

        <div className="row">
            <TextRow label="Copyright" value={props.config.copyright} 
                onChange={val => props.onChange({...props.config, copyright: val })} 
                />
        </div>

        <h2 className="mt-4">Features</h2>

        <FeatureEditor
            name="Cash Bids - Show State Code"
            isChecked={props.config.features.cashBids.showStateCode.enabled}
            onChange={isChecked => onFeatureChange(m => m.features.cashBids.showStateCode.enabled = isChecked)}
            />
            
        <FeatureEditor
            name="Show Settlements"
            isChecked={props.config.features.settlements.enabled}
            onChange={isChecked => onFeatureChange(m => m.features.settlements.enabled = isChecked)}
            />
            
        <FeatureEditor
            name="Show Scale Tickets"
            isChecked={props.config.features.scaleTickets.enabled}
            onChange={isChecked => onFeatureChange(m => m.features.scaleTickets.enabled = isChecked)}
            />
        
        <FeatureEditor
            name="Show eSign"
            isChecked={props.config.features.eSign.enabled}
            onChange={isChecked => onFeatureChange(m => m.features.eSign.enabled = isChecked)}
            />
        
        <FeatureEditor
            name="Show Account Balances"
            isChecked={props.config.features.accountBalances.enabled}
            onChange={isChecked => onFeatureChange(m => m.features.accountBalances.enabled = isChecked)}
            />

        <FeatureEditor
            name="Show @ Market toggle"
            isChecked={props.config.features.marketToggle.enabled}
            onChange={isChecked => onFeatureChange(m => m.features.marketToggle.enabled = isChecked)}
            />

        <FeatureEditor
            name="Auto Invite"
            isChecked={props.config.features.autoInvite.enabled}
            onChange={isChecked => onFeatureChange(m => m.features.autoInvite.enabled = isChecked)}
            />
                
        <div className="row">
            <TextRow label="API Key" value={props.config.apiKey} 
                labelCol="col-2"
                col="col-10"
                onChange={val => props.onChange({...props.config, apiKey: val })} 
                help="Used for retrieving bids when logged out"
                />
        </div>

        <h2 className="mt-4">App Info</h2>

        <div className="row">
            <TextRow label="App Name" value={props.config.appName}
                onChange={val => props.onChange({...props.config, appName: val})}
                labelCol="col-2"
                col="col-4"
                help="Merchant Name, as it will appear on the app"
                placeholder={props.config.name}
            />
            <TextRow label="Display Name" value={props.config.displayName}
                onChange={val => props.onChange({...props.config, displayName: val})}
                labelCol="col-2"
                col="col-4"
                help="App title as it will appear in the app store"
            />
        </div>

        <div className="row">
            <TextRow label="Client" value={props.config.client}
                onChange={val => props.onChange({...props.config, client: val})}
                labelCol="col-2"
                col="col-4"
                help="Internal merchant name without spaces to find configuration"
            />
            <EditorRow label="Theme" labelCol="col-2">
                <div className="col-4">
                    <TypedSelect
                        value={props.config.theme}
                        options={MobileThemeOptions}
                        valueGetter={m => m}
                        textGetter={m => m}
                        onValueSelected={val => props.onChange({...props.config, theme: val})}
                        />
                </div>
            </EditorRow>
        </div>

        <div className="row">
            <TextRow label="Logo URL" value={props.config.logoUrl}
                onChange={val => props.onChange({...props.config, logoUrl: val})}
                placeholder="https://"
                labelCol="col-2"
                col="col-10"
            />
        </div>

        <div className="row">
            <TextRow label="App Icon URL" value={props.config.appIconUrl}
                onChange={val => props.onChange({...props.config, appIconUrl: val})}
                placeholder="https://"
                labelCol="col-2"
                col="col-10"
            />
        </div>

        <div className="row">
            <TextRow label="Splash Screen URL" value={props.config.splashScreenUrl}
                onChange={val => props.onChange({...props.config, splashScreenUrl: val})}
                placeholder="https://"
                labelCol="col-2"
                col="col-10"
            />
        </div>
    </div>;
}

interface EditorRowProps {
    label: string;
    labelCol?: string;
    help?: string;
}
const EditorRow: React.FC<EditorRowProps> = props =>  <>
    <div className={props.labelCol ?? "col-1"}>
        <label>{props.label}
            <InfoIcon text={props.help} />
        </label>
    </div>
    {props.children}
</>;

interface TextRowProps extends EditorRowProps {
    value: string;
    onChange(text: string): void;
    placeholder?: string;
    col?: string;
}
const TextRow: React.FC<TextRowProps> = props => 
<EditorRow label={props.label} labelCol={props.labelCol} help={props.help}>
    <div className={props.col ?? "col-11"}>
        <Form.Control type="text"
            value={props.value}
            onChange={ev => props.onChange(ev.target.value)}
            placeholder={props.placeholder}
        />
    </div>
</EditorRow>

interface FeatureEditorProps {
    name: string;
    description?: string;
    isChecked?: boolean;
    disabled?: boolean;
    onChange(isChecked: boolean): void;
}
const FeatureEditor: React.FC<FeatureEditorProps> = props => {

    return <div className="feature-editor">
        <input type="checkbox" className="mr-2" disabled={props.disabled}
            checked={props.isChecked} 
            onChange={ev => props.onChange(ev.target.checked)}
        />
        <span className="feature-name">{props.name}</span>
        {props.children && 
            <div className="pl-4">
                {props.children}
            </div>
        }
    </div>
}


class MerchantAddress implements Address {
    line1: string;
    line2: string;
    city: string;
    region: string;
    country: string = "United States";
    postalCode: string;
}



export default MobileAppConfigurationEditor;